/* MODULES */
import React, { Suspense } from 'react';
import {
  // BrowserRouter as Router,
  Router,
  Switch,
  Route,
  Redirect,
  // Link,
} from 'react-router-dom';
import { withRouter } from 'react-router';
import { renderToStaticMarkup } from "react-dom/server";
import { connect } from "react-redux";
import { compose } from 'redux';
import queryString from 'query-string';
import { withCookies } from 'react-cookie';

// LANGUAGES
import { withLocalize } from "react-localize-redux";
import deGlobal from "./_translations/de.global.json";
import enGlobal from "./_translations/en.global.json";
import frGlobal from "./_translations/fr.global.json";

/* LOCAL */
// import { mailFolderListItems, otherMailFolderListItems } from './tileData';
import { history } from './_helpers';

import { uiSetCountry } from './_actions';
import { bootstrapAppData } from './_actions/app.actions';

/* components */
import DashboardLayout from './layouts/DashboardLayout';
import { initMatomo } from './_services';

// pages — lazy-loaded für Code-Splitting (C1)
const Home            = React.lazy(() => import('./pages/home').then(m => ({ default: m.Home })));
const Cart            = React.lazy(() => import('./pages/cart').then(m => ({ default: m.Cart })));
const Node            = React.lazy(() => import('./pages/node').then(m => ({ default: m.Node })));
const Asset           = React.lazy(() => import('./pages/asset').then(m => ({ default: m.Asset })));
const Messages        = React.lazy(() => import('./pages/messages').then(m => ({ default: m.Messages })));
const Downloads       = React.lazy(() => import('./pages/downloads').then(m => ({ default: m.Downloads })));
const Profile         = React.lazy(() => import('./pages/profile').then(m => ({ default: m.Profile })));
const Download        = React.lazy(() => import('./pages/download').then(m => ({ default: m.Download })));
const Search          = React.lazy(() => import('./pages/search').then(m => ({ default: m.Search })));
const Login           = React.lazy(() => import('./pages/login').then(m => ({ default: m.Login })));
const Signup          = React.lazy(() => import('./pages/signup').then(m => ({ default: m.Signup })));
const Orders          = React.lazy(() => import('./pages/orders').then(m => ({ default: m.Orders })));
const AddOrder        = React.lazy(() => import('./pages/order').then(m => ({ default: m.AddOrder })));
const EditOrder       = React.lazy(() => import('./pages/order').then(m => ({ default: m.EditOrder })));
const Confirm         = React.lazy(() => import('./pages/signup/confirm').then(m => ({ default: m.Confirm })));
const ResetPassword   = React.lazy(() => import('./pages/reset_password').then(m => ({ default: m.ResetPassword })));
const AdminUsers              = React.lazy(() => import('./pages/admin/Users'));
const AdminRegistrations      = React.lazy(() => import('./pages/admin/Registrations'));
const AdminUnconfirmedUsers   = React.lazy(() => import('./pages/admin/UnconfirmedUsers'));
const AdminMetadata           = React.lazy(() => import('./pages/admin/Metadata'));
const AdminTranslations       = React.lazy(() => import('./pages/admin/Translations'));
const AdminSettings           = React.lazy(() => import('./pages/admin/Settings'));
const AdminAssetStats         = React.lazy(() => import('./pages/admin/AssetStats'));
const AdminStatistics         = React.lazy(() => import('./pages/admin/Statistics'));
const AdminAcceptUser         = React.lazy(() => import('./pages/admin/AcceptUser'));
const Contact       = React.lazy(() => import('./pages/static').then(m => ({ default: m.Contact })));
const Imprint       = React.lazy(() => import('./pages/static').then(m => ({ default: m.Imprint })));
const Privacy       = React.lazy(() => import('./pages/static').then(m => ({ default: m.Privacy })));
const Cookies       = React.lazy(() => import('./pages/static').then(m => ({ default: m.Cookies })));
const TermsOfService = React.lazy(() => import('./pages/static').then(m => ({ default: m.TermsOfService })));
const SetLang       = React.lazy(() => import('./pages/lang').then(m => ({ default: m.SetLang })));
const ErrorPage     = React.lazy(() => import('./pages/ErrorPage').then(m => ({ default: m.ErrorPage })));

function getStoredUser() {
  try {
    return JSON.parse(localStorage.getItem('user'));
  } catch (_error) {
    return null;
  }
}

function isAdminUser(user) {
  return Boolean(
    user &&
    Array.isArray(user.roles) &&
    user.roles.some(role => String(role).toUpperCase() === 'ADMIN')
  );
}

function isVerySecureUser(user) {
  return Boolean(user && (user.jde || user.isSuperUser));
}

const AuthenticatedApp = () => {
  const user = getStoredUser();

  if (!user) {
    return (
      <Redirect
        to={{
          pathname: "/login",
        }}
      />
    );
  }

  return (
    <DashboardLayout>
      <Switch>
        <Route exact path='/' component={Home} />
        <Route exact path='/search/:query' component={Search} />
        <Route exact path='/asset/:id([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})' component={Asset} />
        <Route exact path='/cart' component={Cart} />
        <Route exact path='/messages' component={Messages} />
        <Route exact path='/downloads' component={Downloads} />
        <Route exact path='/profile' component={Profile} />

        <Route
          exact
          path='/orders'
          render={props => (
            isVerySecureUser(user)
              ? <Orders {...props} />
              : <Redirect to='/' />
          )}
        />
        <Route
          exact
          path='/orders/:step'
          render={props => (
            isVerySecureUser(user)
              ? <Orders {...props} />
              : <Redirect to='/' />
          )}
        />
        <Route
          exact
          path='/order/add'
          render={props => (
            isVerySecureUser(user)
              ? <AddOrder {...props} />
              : <Redirect to='/' />
          )}
        />
        <Route
          exact
          path='/order/:id'
          render={props => (
            isVerySecureUser(user)
              ? <EditOrder {...props} />
              : <Redirect to='/' />
          )}
        />
        <Route
          exact
          path='/order/:id/:step'
          render={props => (
            isVerySecureUser(user)
              ? <EditOrder {...props} />
              : <Redirect to='/' />
          )}
        />

        <Route
          exact
          path='/manage/users'
          render={props => (
            isAdminUser(user)
              ? <AdminUsers {...props} />
              : <Redirect to='/' />
          )}
        />
        <Route
          exact
          path='/manage/registrations'
          render={props => (
            isAdminUser(user)
              ? <AdminRegistrations {...props} />
              : <Redirect to='/' />
          )}
        />
        <Route
          exact
          path='/manage/unconfirmed-emails'
          render={props => (
            isAdminUser(user)
              ? <AdminUnconfirmedUsers {...props} />
              : <Redirect to='/' />
          )}
        />
        <Route
          exact
          path='/manage/metadata'
          render={props => (
            isAdminUser(user)
              ? <AdminMetadata {...props} />
              : <Redirect to='/' />
          )}
        />
        <Route
          exact
          path='/manage/translations'
          render={props => (
            isAdminUser(user)
              ? <AdminTranslations {...props} />
              : <Redirect to='/' />
          )}
        />
        <Route
          exact
          path='/manage/settings'
          render={props => (
            isAdminUser(user)
              ? <AdminSettings {...props} />
              : <Redirect to='/' />
          )}
        />
        <Route
          exact
          path='/manage/asset-stats'
          render={props => (
            isAdminUser(user)
              ? <AdminAssetStats {...props} />
              : <Redirect to='/' />
          )}
        />
        <Route
          exact
          path='/manage/statistics'
          render={props => (
            isAdminUser(user)
              ? <AdminStatistics {...props} />
              : <Redirect to='/' />
          )}
        />

        <Route
          exact
          path='/administration/accept-user/:id([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})'
          render={props => (
            isAdminUser(user)
              ? <AdminAcceptUser {...props} />
              : <Redirect to='/' />
          )}
        />

        <Route path='/:id([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})' component={Node} />
        <Route component={ErrorPage} />
      </Switch>
    </DashboardLayout>
  );
};

class App extends React.Component {

  constructor(props){
    super(props);
    const languages = [
      { name: "German", code: "de" },
      { name: "English", code: "en" },
      { name: "French", code: "fr" },
    ];
    const onMissingTranslation = ({ defaultTranslation }) => defaultTranslation;
    const defaultLanguage = window.localStorage.getItem("lang") || languages[0];
    // const defaultLanguage = this.props.lang || languages[0];

    this.props.initialize({
      languages,
      translation: deGlobal,
      options: { renderToStaticMarkup, defaultLanguage, onMissingTranslation }
    });
    this.state = {
      piwik: {
        connectToHistory: () => history => { return history; },
      },
    }
  }

  componentDidMount(){
    this.props.addTranslationForLanguage(deGlobal, "de");
    this.props.addTranslationForLanguage(enGlobal, "en");
    this.props.addTranslationForLanguage(frGlobal, "fr");
    
    // Lade User-Info und MetadataItems beim App-Start (nur wenn User eingeloggt ist)
    if (localStorage.getItem('user')) {
      this.props.bootstrapAppData();
    }

    const qs = queryString.parse(this.props.location.search);
    const c = {
      "de": "germany",
      "at": "austria",
      "ch": "switzerland"
    }
    const countryParam = qs.country || qs.c;
    if(countryParam){
      this.props.setCountry(c[countryParam])
    }
    initMatomo();
    // const trackingConsent = (cookies.get("tracking") === "true") || false;
    // if (trackingConsent) { 
    //   const piwik = initMatomo();
    //   this.setState({ piwik });
    // }
  }

  render() {
    const publicRoutes = (
      <Switch>
        <Route exact path='/de' render={()=><SetLang lang="de" />} />
        <Route exact path='/en' render={()=><SetLang lang="en" />} />
        <Route exact path='/fr' render={()=><SetLang lang="fr" />} />
        <Route exact path='/download/:filename' component={Download} />
        <Route exact path='/login' component={Login} />
        <Route exact path='/reset_password/:uuid' component={ResetPassword} />
        <Route exact path='/signup' component={Signup} />
        <Route exact path='/confirm/:uuid' component={Confirm} />

        <Route exact path='/contact' component={Contact} />
        <Route exact path='/imprint' component={Imprint} />
        <Route exact path='/privacy' component={Privacy} />
        <Route exact path='/cookies' component={Cookies} />
        <Route exact path='/termsofservice' component={TermsOfService} />

        <Route path='/' component={AuthenticatedApp} />
      </Switch>
    );
    
    return (
      <div>
        <Suspense fallback={<div style={{ display: 'flex', justifyContent: 'center', paddingTop: 80 }}>Laden…</div>}>
        {this.state.trackingConsent
          ? <Router history={this.state.piwik.connectToHistory(history)}>{publicRoutes}</Router>
          : <Router history={history}>{publicRoutes}</Router>
        }
        </Suspense>
        {/* <CookieConsent
          location="top"
          buttonText={this.props.translate("cookieConsent.btnText")}
          cookieName="CookieConsentAccepted"
          style={{ background: "#2B373B", height: 64, zIndex: 10001 }}
          buttonStyle={{ color: "#4e503b", fontSize: "13px" }}
          expires={150}
        >
          <Translate
            id="cookieConsent.text"
            data={{
              cookies: <LinkWithText {...this.props} target="cookies"/>,
              privacy: <LinkWithText {...this.props} target="privacy"/>,
              termsofservice: <LinkWithText {...this.props} target="termsofservice"/>
            }}
          /> 
        </CookieConsent> */}
      </div>
    );
  }
}

const mapStateToProps = state => {
  return {
    lang: state.lang,
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    setCountry: (country) => dispatch(uiSetCountry(country)),
    bootstrapAppData: () => dispatch(bootstrapAppData()),
  };
};

const AppComponent = compose(
  withLocalize,
  withRouter,
  withCookies,
  connect(mapStateToProps, mapDispatchToProps),
)(App)

// export default withStyles(styles, { withTheme: true })(App);
export default AppComponent;
